import Subscription from '@aytm/lib/common/subscription'

import { ChatMessage } from '../../types'
import { StatsChatAssistant as TStatsChatAssistant, StatsChatAssistantState } from './types'
import { DEFAULT_POLL_MS, DISCLAIMER_TEXT, IDLE_POLL_MS } from '../common/const'
import { SKIPPER_AUTHOR_ID } from '../../const'
import ChatError from '../../common/ChatError'
import buildPollingService from '../common/pollingService'
import awaitContext from './lib/awaitContext'

import initAction from '../common/init'
import sendAction from '../common/send'
import commandAction from '../common/command'
import cancelResponseAction from '../common/cancelResponse'
import rateMessageAction from '../common/rateMessage'

import statsChatAssistantMessageExtensions from './messageExtensions'

import SkipperGreetingCard from '../../chat/components/SkipperGreetingCard'
import { GLOBAL_SUGGESTED_PROMPTS_SECTION } from '../../chat/globalSuggestedPrompts'

// Sent verbatim as the user message — the wording is the intent signal that routes Skipper's
// commutator. The Guided Reports pill is not among them: it is gated on the survey's own feature
// state, so the host page supplies it through guidedReportsPillGetter or it is not offered at all.
const STATS_SUGGESTED_PROMPTS = [
  { label: 'Summarize open-ended responses' },
  { label: 'Find themes for my open-ended responses' },
  { label: 'Compare sentiment across segments' }
]

export default function StatsChatAssistant(): TStatsChatAssistant {
  const assistantState: StatsChatAssistantState = {
    ready: false,
    opened: false,
    inputReady: false,
    chatID: null,
    userName: null,
    limits: null,
    messages: [],
    hasUnreadMessages: false,
    currentPrompt: '',
    promptSeed: 0,
    connectionStatus: 'ok',
    contextGetter: null,
    guidedReportsPillGetter: null,
    pollingService: null,
    subscription: null,
    showDisclaimer: false,
    initFailed: false,
    pendingGuidedReport: null
  }

  let assistantInitializing = false
  const readMessageIDs: Set<string> = new Set()

  let assistant: TStatsChatAssistant = null

  const update: () => void = () => {
    if (!assistantState.opened && !assistantState.hasUnreadMessages) {
      assistantState.hasUnreadMessages = assistantState.messages.some(message => !readMessageIDs.has(message.id))
      if (assistantState.hasUnreadMessages) {
        // eslint-disable-next-line no-console
        console.debug('StatsChatAssistant: has unread messages', assistant.id)
      }
    } else {
      assistantState.hasUnreadMessages = false
    }
    assistantState.subscription?.update(assistant)
  }

  assistantState.pollingService = buildPollingService({
    assistantState,
    update,
    awaitContext
  }) as typeof assistantState.pollingService

  assistant = {
    get id() {
      return 'stats'
    },
    get name() {
      return 'Skipper-chat'
    },
    get icon() {
      return 'icon-aytmfd-skipper'
    },
    get ready() {
      return assistantState.ready
    },
    async init(newChat = false) {
      if (assistantInitializing) {
        return new ChatError('Chat assistant is already initializing')
      }
      assistantState.showDisclaimer = false
      update()
      assistantInitializing = true
      const initResult = await initAction({
        assistantState,
        update,
        newChat,
        domain: 'stats',
        awaitContext,
        debugName: 'StatsChatAssistant'
      })
      assistantInitializing = false
      // A failure leaves the assistant "settled" with a read-only input, which is indistinguishable
      // from a working chat on `ready` alone — record it so an entry point can restart instead.
      assistantState.initFailed = initResult instanceof ChatError
      update()
      return initResult
    },
    get initFailed() {
      return assistantState.initFailed
    },
    async send(message: ChatMessage | string, opts?: { suggestedPrompt?: boolean }) {
      if (assistantState.ready) {
        return await sendAction(message, {
          assistantState,
          update,
          awaitContext,
          debugName: 'StatsChatAssistant',
          suggestedPrompt: opts?.suggestedPrompt ?? false
        })
      } else {
        const error: ChatError = new ChatError('Chat assistant is not ready')
        return error
      }
    },
    async command(command, payload = {}) {
      if (assistantState.ready) {
        return await commandAction(command, payload, {
          assistantState,
          update,
          awaitContext,
          debugName: 'StatsChatAssistant'
        })
      } else {
        const error: ChatError = new ChatError('Chat assistant is not ready')
        return error
      }
    },
    async cancelResponse() {
      if (assistantState.ready) {
        return await cancelResponseAction({
          assistantState,
          update,
          awaitContext,
          debugName: 'StatsChatAssistant'
        })
      }
    },
    async rateMessage(messageID, like) {
      if (assistantState.ready) {
        return await rateMessageAction(messageID, like, {
          assistantState,
          update,
          awaitContext,
          debugName: 'StatsChatAssistant'
        })
      } else {
        const error: ChatError = new ChatError('Chat assistant is not ready')
        return error
      }
    },
    get canRateMessage() {
      return true
    },
    get GreetingCard() {
      return SkipperGreetingCard
    },
    get inputPlaceholder() {
      return 'Ask me anything...'
    },
    get currentPrompt() {
      return assistantState.currentPrompt
    },
    set currentPrompt(prompt) {
      assistantState.currentPrompt = prompt
    },
    get promptSeed() {
      return assistantState.promptSeed
    },
    // Seeding is not the same gesture as the user typing: the input takes its own copy of the text
    // once, on mount, so a plain assignment reaches it only when it has yet to open. Advancing the
    // counter is what an already-open input watches for.
    seedPrompt(prompt: string) {
      assistantState.currentPrompt = prompt
      assistantState.promptSeed += 1
      update()
    },
    get chatID() {
      return assistantState.chatID
    },
    set chatID(chatID) {
      assistantState.chatID = chatID
      update()
    },
    get userName() {
      return assistantState.userName
    },
    set userName(userName) {
      assistantState.userName = userName
      update()
    },
    get conversationLimits() {
      return assistantState.limits
    },
    get inputReady() {
      return assistantState.inputReady
    },
    get closed() {
      return false
    },
    get rated() {
      return false
    },
    get messages() {
      return assistantState.messages
    },
    get messageExtensions() {
      return statsChatAssistantMessageExtensions
    },
    get connectionStatus() {
      return assistantState.connectionStatus
    },
    get hasUnreadMessages() {
      return !assistantState.opened && assistantState.hasUnreadMessages
    },
    get suggestedPrompts() {
      const guidedReportPill = assistantState.guidedReportsPillGetter?.() ?? null
      return {
        pageSpecific: {
          label: 'On this page — stats',
          prompts: guidedReportPill ? [guidedReportPill, ...STATS_SUGGESTED_PROMPTS] : STATS_SUGGESTED_PROMPTS
        },
        global: GLOBAL_SUGGESTED_PROMPTS_SECTION
      }
    },
    get contextGetter() {
      return assistantState.contextGetter
    },
    set contextGetter(contextGetter) {
      assistantState.contextGetter = contextGetter
    },
    get guidedReportsPillGetter() {
      return assistantState.guidedReportsPillGetter
    },
    set guidedReportsPillGetter(getter) {
      assistantState.guidedReportsPillGetter = getter
    },
    setGuidedReportEntry(entry) {
      assistantState.pendingGuidedReport = entry ?? null
    },
    get acceptAttachments() {
      return false
    },
    get currentDomain() {
      return 'stats'
    },
    get currentContextId() {
      return assistantState.contextGetter?.()?.survey_id ?? null
    },
    get disclaimer() {
      if (assistantState.showDisclaimer) {
        return DISCLAIMER_TEXT
      } else {
        // check if there is any Skipper messages
        const hasSkipperMessages = assistantState.messages.some(message => message.author === SKIPPER_AUTHOR_ID)
        if (hasSkipperMessages) {
          assistantState.showDisclaimer = true
          return DISCLAIMER_TEXT
        } else {
          return null
        }
      }
    },
    chatOpened() {
      assistantState.opened = true
      readMessageIDs.clear()
      if (IDLE_POLL_MS === assistantState.pollingService.interval) {
        assistantState.pollingService.interval = DEFAULT_POLL_MS
      }
      if (assistantState.hasUnreadMessages) {
        assistantState.hasUnreadMessages = false
        update()
      }
    },
    chatClosed() {
      assistantState.opened = false
      // Drop an armed-but-unsent intent so it cannot ride a later, unrelated turn.
      assistantState.pendingGuidedReport = null
      readMessageIDs.clear()
      assistantState.messages.forEach(message => void readMessageIDs.add(message.id))
      if (assistantState.pollingService.interval > 0) {
        assistantState.pollingService.interval = IDLE_POLL_MS
      }
    },
    listen(listener) {
      if (!assistantState.subscription) {
        assistantState.subscription = Subscription<TStatsChatAssistant>()
      }
      return assistantState.subscription.listen(listener, assistant)
    },
    update() {
      update()
    },
    destroy() {
      if (assistantState.subscription) {
        assistantState.subscription.clear()
        assistantState.subscription = null
      }
    }
  }

  return assistant
}
